import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  Grid,
  InputAdornment,
  Link,
  Paper,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { ColumnDef } from "@tanstack/react-table";
import * as React from "react";
import { useAdminStore } from "../../../../context/admin-store";
import { useCategory } from "../../../../context/category-store";
import { getAll } from "../../../../services/commissions.service";
import SectionTitle from "../../../../ui-components/SectionTitle";
import { parseDate } from "../../../../utils/parseDate";
import { adminButtonPrimarySx } from "../../modalStyle";
import UpdateButton from "./OperationButtons/UpdateButton";
import {
  ExportColumnConfig,
  ExportDialog,
} from "../../../ui/ExportDialog";
import { DataTable } from "../../../ui/DataTable";

const columns: ColumnDef<any>[] = [
  {
    accessorKey: "id",
    header: "ID",
  },
  {
    accessorKey: "name",
    header: "Dono",
  },
  {
    accessorKey: "brand_name",
    header: "Marca",
  },
  {
    accessorKey: "instagram_url",
    header: "Instagram",
    cell: (info) => info.getValue(),
  },
  {
    accessorKey: "category",
    header: "Categoria",
  },
  {
    accessorKey: "amount",
    header: "Valor",
  },
  {
    accessorKey: "is_paid",
    header: "Pago",
  },
  {
    accessorKey: "date",
    header: "Data Declarada",
  },
  {
    accessorKey: "actions",
    header: "Ações",
    cell: (info) => info.getValue(),
  },
];

const createRows = (
  events: any,
  updateSubscriptions: Function,
  users: any,
  categories: any,
) => {
  return events.map(
    ({ event_id, user_id, start_date, id, date, is_paid, amount }, i, arr) => {
      const user = users.find((user) => user.id === user_id);
      const category = categories.find((cat) => cat.id === user?.category_id);
      console.log(events);

      return {
        id,
        event_id,
        name: user?.name,
        brand_name: user?.brand_name,
        amount: "R$" + amount,
        instagram_url: (
          <Link
            target={"_blank"}
            rel={"noopener noreferrer"}
            href={`https://www.instagram.com/${
              user?.instagram_url.split("@")[1]
            }/`}
          >
            {user?.instagram_url}
          </Link>
        ),
        category: (category && category.name) || "",
        is_paid: is_paid ? "Sim" : "Não",
        start_date: parseDate(start_date),
        date: parseDate(date),
        actions: (
          <>
            <UpdateButton
              id={id}
              updateCommisions={updateSubscriptions}
              sub={arr[i]}
            />
          </>
        ),
      };
    },
  );
};

export default function ComissionsTable() {
  const [commissions, setCommissions] = React.useState([]);
  const [selectedEventId, setSelectedEventId] = React.useState<number | null>(
    null,
  );
  const [eventSearch, setEventSearch] = React.useState<string>("");
  const [eventStatusTab, setEventStatusTab] = React.useState<
    "active" | "finished"
  >("active");
  const users = useAdminStore((state) => state.users);
  const categories = useCategory((state) => state.categories);
  const events = useAdminStore((state) => state.events);
  const subs = useAdminStore((state) => state.subscriptions);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [exportOpen, setExportOpen] = React.useState(false);

  const exportColumns: ExportColumnConfig[] = [
    { key: "id", label: "ID" },
    { key: "user_id", label: "Usuario ID" },
    { key: "subscription_id", label: "Inscricao ID" },
    {
      key: "amount",
      label: "Valor",
      formatter: (v) => (v != null ? "R$" + v : ""),
    },
    { key: "pix_code", label: "Codigo PIX" },
    { key: "date", label: "Data" },
    {
      key: "is_paid",
      label: "Pago",
      formatter: (v) => (v ? "Sim" : "Não"),
    },
    { key: "created_at", label: "Criado Em" },
    { key: "updated_at", label: "Atualizado Em" },
    { key: "event_name", label: "Nome do Evento" },
    { key: "user_name", label: "Nome Usuario" },
  ];

  const editedEvents = events.map((event: any) => {
    return {
      ...event,
      commissions: commissions.filter(
        (com) =>
          subs.find((s) => s.id === com.subscription_id)?.event_id === event.id,
      ),
    };
  });
  const statusFilteredEvents = editedEvents.filter((ev: any) =>
    eventStatusTab === "active" ? Boolean(ev?.active) : !ev?.active,
  );
  const visibleEvents = statusFilteredEvents.filter((ev: any) => {
    const normalized = eventSearch.trim().toLowerCase();
    const nameMatch = (ev.name || "").toLowerCase().includes(normalized);
    const dateStr = parseDate(ev.starts_at || "");
    const dateMatch = (dateStr || "").toLowerCase().includes(normalized);
    return !normalized || nameMatch || dateMatch;
  });

  React.useEffect(() => {
    syncCommissions();
  }, []);

  function syncCommissions() {
    getAll().then((x) => {
      console.log(x);
      setCommissions(x);
    });
  }

  React.useEffect(() => {
    if (statusFilteredEvents && statusFilteredEvents.length > 0) {
      if (
        selectedEventId === null ||
        !statusFilteredEvents.some((e: any) => e.id === selectedEventId)
      ) {
        setSelectedEventId(statusFilteredEvents[0].id);
      }
    } else {
      setSelectedEventId(null);
    }
  }, [events, eventStatusTab, selectedEventId, commissions, subs]);

  function getExportData() {
    return commissions.map((obj) => ({
      ...obj,
      event_name:
        events.find(
          (ev) => ev.id === subs.find((sub) => sub.event_id === ev.id)?.event_id,
        )?.name || "EVENTO APAGADO",
      user_name: users.find((ev) => ev.id === obj.user_id)?.name,
    }));
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "stretch" : "center",
          justifyContent: "space-between",
          gap: 2,
          width: "100%",
          mb: 2,
        }}
      >
        <SectionTitle title="Comissões" />
        <Button
          variant="contained"
          onClick={() => setExportOpen(true)}
          sx={adminButtonPrimarySx}
          fullWidth={isMobile}
        >
          EXPORTAR DADOS COMO EXCEL
        </Button>
      </Box>

      {users && categories && events && commissions && (
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            border: "1px solid #e2e8f0",
            borderRadius: 2,
            boxShadow: "0 1px 2px rgba(16,24,40,.06)",
            mb: 3,
          }}
        >
          {editedEvents && editedEvents.length > 0 ? (
            <>
              <Box sx={{ mb: 2 }}>
                <Tabs
                  value={eventStatusTab}
                  onChange={(_, val) => setEventStatusTab(val)}
                  sx={{
                    minHeight: 36,
                    "& .MuiTab-root": {
                      minHeight: 36,
                      textTransform: "none",
                      fontWeight: 600,
                    },
                  }}
                >
                  <Tab
                    value="active"
                    label={`Eventos ativos (${
                      (editedEvents || []).filter((e: any) => Boolean(e?.active))
                        .length
                    })`}
                  />
                  <Tab
                    value="finished"
                    label={`Eventos finalizados (${
                      (editedEvents || []).filter((e: any) => !e?.active).length
                    })`}
                  />
                </Tabs>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column-reverse", md: "row" },
                  alignItems: { xs: "stretch", md: "center" },
                  justifyContent: "space-between",
                  gap: 2,
                  mb: 2,
                }}
              >
                <Tabs
                  value={selectedEventId ?? false}
                  onChange={(_, val) => setSelectedEventId(val)}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label="Eventos"
                  sx={{
                    borderBottom: "1px solid #e2e8f0",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                    {visibleEvents.map((event: any) => (
                      <Tab
                        key={event.id}
                        label={`${event.name} - ${parseDate(event.starts_at)}`}
                        value={event.id}
                      />
                    ))}
                </Tabs>
                <TextField
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  placeholder={"Pesquisar eventos"}
                  size="small"
                  sx={{ width: { xs: "100%", md: 360 } }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {visibleEvents.length === 0 ? (
                <Box py={4} textAlign="center">
                  Nenhum evento encontrado.
                </Box>
              ) : (
                selectedEventId !== null && (
                  <Grid container my={3}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          height: isMobile ? 420 : 660,
                          width: "100%",
                          mb: 3,
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ color: "#24324D", mb: 1 }}
                        >
                          Valor total de comissões do evento:{" "}
                          <span style={{ color: "green" }}>
                            R${" "}
                            {statusFilteredEvents
                              .find((ev: any) => ev.id === selectedEventId)
                              ?.commissions.reduce(
                                (x: number, y: any) => (x += +y.amount),
                                0,
                              ) || 0}
                          </span>
                        </Typography>
                        <DataTable
                          data={createRows(
                            statusFilteredEvents.find(
                              (ev: any) => ev.id === selectedEventId,
                            )?.commissions || [],
                            syncCommissions,
                            users,
                            categories,
                          )}
                          columns={columns}
                          dense={isMobile}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                )
              )}
            </>
          ) : (
            <Box py={4} textAlign="center">
              Nenhum evento disponível.
            </Box>
          )}
        </Paper>
      )}
      <ExportDialog
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        title="Exportar Comissões"
        data={getExportData()}
        columns={exportColumns}
        defaultFileName="Comissoes.xlsx"
        sheetName="Comissoes"
      />
    </>
  );
}

/* 

import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';


<Grid container my={3}>
              <Grid item xs={12}>
                <SectionTitle title="Tabela de Comissões" />
                <div style={{ height: 660, width: "100%" }}>
                  {users && <DataGrid
                    rows={createRows(
                      commissions,
                      syncCommissions,
                      users,
                      categories
                    )}
                    columns={columns}
                    pageSize={10}
                    rowsPerPageOptions={[10]}
                    checkboxSelection={false}
                  />}
                </div>
              </Grid>
            </Grid>

*/
